import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormField, Input, Select, Textarea } from "../UI/FormField";
import Button from "../UI/Button";
import { Activity, ActivityType, ReminderOption } from "../../types/tasks";
import {
  ACTIVITY_TYPES,
  REMINDER_OPTIONS,
} from "../../constants/taskConstants";

interface Props {
  taskId: string;
  initial?: Activity | null;
  onSave: (data: Activity) => void;
  onCancel: () => void;
}

const mkId = () => `a_${Math.random().toString(36).slice(2, 10)}`;
const nowIso = () => new Date().toISOString();

export const ActivityForm: React.FC<Props> = ({
  taskId,
  initial,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation("tasks");
  const [draft, setDraft] = useState<Activity>({
    id: mkId(),
    taskId,
    type: "call",
    title: "",
    reminder: "none",
    createdAt: nowIso(),
    updatedAt: nowIso(),
  });
  const [titleError, setTitleError] = useState(false);

  useEffect(() => {
    if (initial) setDraft({ ...initial });
  }, [initial]);

  const patch = (p: Partial<Activity>) =>
    setDraft((prev) => ({ ...prev, ...p }));

  const handleSave = () => {
    if (!draft.title.trim()) {
      setTitleError(true);
      return;
    }
    onSave({ ...draft, updatedAt: nowIso() });
  };

  return (
    <div className="border border-gray-200 rounded-md bg-white p-3 space-y-1">
      <FormField layout="inline" size="sm" label={t("form.type")}>
        <Select
          size="sm"
          value={draft.type}
          onChange={(e) => patch({ type: e.target.value as ActivityType })}
        >
          {ACTIVITY_TYPES.map((ty) => (
            <option key={ty} value={ty}>
              {t(`activity.types.${ty}`)}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField
        layout="inline"
        size="sm"
        label={t("activity.title")}
        required
      >
        <Input
          type="text"
          size="sm"
          value={draft.title}
          onChange={(e) => {
            setTitleError(false);
            patch({ title: e.target.value });
          }}
          placeholder={t("activity.titlePlaceholder")}
          error={titleError}
        />
      </FormField>

      <FormField layout="inline" size="sm" label={t("activity.notes")}>
        <Textarea
          size="sm"
          rows={2}
          value={draft.notes ?? ""}
          onChange={(e) => patch({ notes: e.target.value })}
          placeholder={t("activity.notesPlaceholder")}
        />
      </FormField>

      <FormField layout="inline" size="sm" label={t("activity.dueDate")}>
        <Input
          type="date"
          size="sm"
          value={draft.dueDate ?? ""}
          onChange={(e) => patch({ dueDate: e.target.value || undefined })}
        />
      </FormField>

      <FormField layout="inline" size="sm" label={t("activity.dueTime")}>
        <Input
          type="time"
          size="sm"
          value={draft.dueTime ?? ""}
          onChange={(e) => patch({ dueTime: e.target.value || undefined })}
        />
      </FormField>

      <FormField layout="inline" size="sm" label={t("activity.reminder")}>
        <Select
          size="sm"
          value={draft.reminder}
          onChange={(e) =>
            patch({ reminder: e.target.value as ReminderOption })
          }
        >
          {REMINDER_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {t(`activity.reminderOptions.${r}`)}
            </option>
          ))}
        </Select>
      </FormField>

      {initial && (
        <FormField layout="inline" size="sm" label={t("activity.result")}>
          <Textarea
            size="sm"
            rows={2}
            value={draft.result ?? ""}
            onChange={(e) => patch({ result: e.target.value })}
            placeholder={t("activity.resultPlaceholder")}
          />
        </FormField>
      )}

      <div className="flex justify-end items-center gap-2 pt-2 mt-1 border-t border-gray-100">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          {t("activity.cancel")}
        </Button>
        <Button type="button" variant="primary" size="sm" onClick={handleSave}>
          {t("activity.save")}
        </Button>
      </div>
    </div>
  );
};
